import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasyHeaderLabel from '../../components/lables/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import { useNavigate } from 'react-router-dom';
import '../../styles/styles.css';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import {
  clearStoreState,
  deactivateStore,
  downloadStores,
  fetchStores,
  reactivateStore,
  storesState
} from '../../redux_store/slices/storesSlice';
import { formatDateTime } from '../../utils/DateUtils';
import { UNAUTHORIZED } from '../../constants/Constants';
import { authActions } from '../../redux_store/slices/authSlice';
import {
  BakeasyButtonExport,
  BakeasyButtonFilter,
  BakeasyButtonAdd,
  BakeasyButtonPrint
} from '../../components/buttons/BakeasyButton';
import BakeasySwitch from '../../components/common/BakeasySwitch';
import { BakeasyChipEdit } from '../../components/common/BakeasyChip';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { clearStoreProductState } from '../../redux_store/slices/storeProductSlice';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { t } from 'i18next';
import { storeTableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Stores: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [storeList, setStoreList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } = useAppSelector(storesState);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload = await preparePayload(
      { limit: rowsPerPage, page: newPage + 1, sortBy: 'createdDate', sortDirection: -1 },
      filter,
      search,
      newPage
    );
    await dispatch(fetchStores(payload));
    // await fetchData(fetchStores, payload);

  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((store: any) => {
        return {
          storeNumber: store?.storeNumber,
          storeName: store?.storeName ?? '',
          onStoreNameClick: () => {
            dispatch(clearStoreState());
            navigate('/addEditStore', { state: { isView: true, isEdit: false, _id: store?._id } });
          },
          contact: (store?.contact1?.firstName ?? '') + ' ' + (store?.contact1?.lastName ?? ''),
          onContactClick: () => {
            openContactStoreConfirmDialog(store);
          },
          createdDate: store?.createdDate ? formatDateTime(store?.createdDate) : '',
          lastAccess: store?.lastAccess ? formatDateTime(store?.lastAccess) : '',
          totalOrderValue:
            store?.totalOrderValue || store?.totalOrderValue === 0
              ? parseFloat(store?.totalOrderValue).toFixed(2)
              : '',
          totalOrders: store?.totalOrders ?? '',
          assignedProducts: store?.assignedProducts ?? '',
          onAssignedProductsClick: () => {
            dispatch(clearStoreProductState());
            navigate('/storeProducts', {
              state: { storeEntityId: store?.storeEntityId, name: store?.storeName }
            });
          },
          status: (
            <BakeasySwitch
              value={store?.status === 'Active' ? true : false}
              onChange={(event: any) => {
                event.stopPropagation();
                store?.status === 'Active'
                  ? openDeActivateStoreConfirmDialog(store?._id)
                  : openReActivateStoreConfirmDialog(store?._id);
              }}
            />
          ),
          actions: [
            <BakeasyChipEdit
              key={store?._id}
              onClick={(event: any) => {
                dispatch(clearStoreState());
                navigate('/addEditStore', {
                  state: { isEdit: true, isView: false, _id: store?._id }
                });
              }}
            />
          ]
        };
      });

      setStoreList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  function openFilterStoreConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'store-filter',
      open: true,
      title: t('STORE_FILTER'),
      data: filter,
      onNoClick: (event: any) => { },
      onYesClick: async (event: any, param: any) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      }
    });
  }

  function openContactStoreConfirmDialog(store: any) {
    myContext.setConfirmDialog({
      type: 'contact',
      open: true,
      title: t('CONTACT_STORE'),
      data: store?.contact1
    });
  }

  function openReActivateStoreConfirmDialog(_id: any) {
    myContext.setConfirmDialog({
      type: 'reActivate',
      open: true,
      title: t('REACTIVATE_STORE'),
      message: t('REACTIVATE_STORE_MESSAGE'),
      onNoClick: async () => { },
      onYesClick: async (event: any, param: string | undefined) => {
        await dispatch(reactivateStore({ id: _id }));
        await refreshPageData(pageNo - 1);
      }
    });
  }

  function openDeActivateStoreConfirmDialog(_id: any) {
    myContext.setConfirmDialog({
      type: 'deActivate',
      open: true,
      title: t('DEACTIVATE_STORE'),
      message: t('DEACTIVATE_STORE_MESSAGE'),
      onNoClick: async () => { },
      onYesClick: async (event: any, param: string | undefined) => {
        await dispatch(deactivateStore({ id: _id }));
        await refreshPageData(pageNo - 1);
      }
    });
  }

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function storesUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={'STORES'} />

          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={9} lg={6} xl={6}>
              <div className="tableOptions">
                <BakeasyButtonPrint
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadStores({ ...filter, downloadType: 'pdf' }));
                  }}
                />

                <BakeasyButtonExport
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadStores({ ...filter, downloadType: 'csv' }));
                  }}
                />

                <BakeasyButtonFilter
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openFilterStoreConfirmDialog();
                  }}
                />

                <BakeasyButtonAdd
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(clearStoreState());
                    navigate('/addEditStore', {
                      state: { isNew: true, isView: false, isEdit: false }
                    });
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />

          <Grid>
            {storeList.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={storeTableHeaders}
                tableData={storeList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return storesUI();
};
